import type { InventoryItem, Level } from '../api/types';

/**
 * The order a tap walks through.
 *
 * Three rungs, not four. The middle of a four-rung ladder — "some" against "low" — was a
 * judgement call nobody made the same way twice, and it put the useful end of the cycle three
 * taps from the top. What the household actually distinguishes is plenty, running out, and
 * none.
 *
 * It wraps, and the wrap is now the point rather than a safety net: `out` is where a line
 * waits to be filled again, and the tap that fills it is the same one that emptied it.
 */
const CYCLE: readonly Level[] = ['plenty', 'low', 'out'];

export function cycled(level: Level): Level {
  const next = CYCLE[(CYCLE.indexOf(level) + 1) % CYCLE.length];

  // Unreachable: the index is taken modulo the length of the same array.
  return next ?? 'plenty';
}

/** Whether this level should put the item on the shopping list. */
export function needsRestocking(level: Level): boolean {
  return level === 'low' || level === 'out';
}

/**
 * Whether the household has run out of this.
 *
 * A stock line at zero, not a line on its way out. Running out used to retire a perishable in
 * the same tap, which threw away the one thing worth keeping: the household's own statement
 * that they buy this. The row stays, at the bottom of the kitchen under its own heading, and
 * one tap on it says the food is back.
 *
 * Everything that reads a date has to ask this first. An empty line's dates describe food that
 * is gone, so it is not overdue, it is not nearly overdue, and there is nothing to ask about
 * it.
 */
export function isOutOfStock(item: { level: Level }): boolean {
  return item.level === 'out';
}

/**
 * Whether this tap is putting stock back on a line that had run out.
 *
 * Which is a restock, not a level change — see `Actions.cycleLevel`. The distinction is the
 * date: the line has been empty for however long, and coming back as `plenty` with the old
 * date would put fresh milk straight into "Use now".
 */
export function refills(item: InventoryItem, to: Level): boolean {
  return isOutOfStock(item) && to !== 'out';
}

const WORDING: Record<Level, string> = {
  plenty: 'Plenty',
  low: 'Low',
  out: 'None',
};

export function levelWording(level: Level): string {
  return WORDING[level];
}
